import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Pin, Sparkles } from 'lucide-react';
import { useSlotAssetGeneratorStore } from '../../store/slotAssetGeneratorStore';
import { GOLDEN_PROMPT_SUFFIX } from '../../../core/types/slot-asset-generator.types';
import type { SlotAssetGenerationRequest } from '../../../core/types/slot-asset-generator.types';
import { useSlotAssetGeneratorStore as useImageSettingsStore } from '../../store/slotAssetGeneratorStore';
import { buildStructuredSlotPrompt } from '../../services/slot-asset-generator/SlotAssetGeneratorService';

const IMAGE_COUNT_OPTIONS = [1, 2, 4, 8] as const;

interface SlotPromptPanelProps {
  onGenerate: (request: SlotAssetGenerationRequest) => void;
}

export const SlotPromptPanel: React.FC<SlotPromptPanelProps> = ({ onGenerate }) => {
  const selectedComponentId = useSlotAssetGeneratorStore((s) => s.selectedComponentId);
  const components = useSlotAssetGeneratorStore((s) => s.components);
  const updateComponent = useSlotAssetGeneratorStore((s) => s.updateComponent);

  const style = useImageSettingsStore((s) => s.imageSettings.style);
  const quality = useImageSettingsStore((s) => s.imageSettings.qualityPreset);
  const model = useImageSettingsStore((s) => s.imageSettings.model);

  const [imageCount, setImageCount] = useState<1 | 2 | 4 | 8>(1);
  const [systemContextEdited, setSystemContextEdited] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const systemTextareaRef = useRef<HTMLTextAreaElement>(null);

  const selectedComponent = selectedComponentId
    ? components.find((c) => c.id === selectedComponentId) ?? null
    : null;

  const systemContextText = useMemo(() => {
    if (!selectedComponent) return '';
    return buildStructuredSlotPrompt({
      component: selectedComponent,
      prompt: '',
      sessionReferenceImages: [],
      imageCount,
      style,
      quality,
      model,
    }).systemContext;
  }, [selectedComponent, style, quality, model, imageCount]);

  // Reset system context override when dependencies change
  useEffect(() => { setSystemContextEdited(null); }, [selectedComponent?.id, style, quality, model]);

  // Auto-resize system context textarea to fit content
  const systemContextValue = systemContextEdited ?? systemContextText;
  useEffect(() => {
    const el = systemTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [systemContextValue]);

  // Auto-fill golden variant prompt
  useEffect(() => {
    if (!selectedComponent?.deriveFrom || selectedComponent.prompt) return;
    const base = components.find((c) => c.id === selectedComponent.deriveFrom);
    if (base?.prompt) {
      updateComponent(selectedComponent.id, {
        prompt: `${base.prompt}, ${GOLDEN_PROMPT_SUFFIX}`,
      });
    }
  }, [selectedComponent?.id, selectedComponent?.deriveFrom]);

  const prompt = selectedComponent?.prompt ?? '';

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (!selectedComponent) return;
      updateComponent(selectedComponent.id, { prompt: e.target.value });
    },
    [selectedComponent, updateComponent]
  );

  const handleGenerate = useCallback(() => {
    if (!selectedComponent || !prompt.trim()) return;
    onGenerate({
      component: selectedComponent,
      prompt: prompt.trim(),
      sessionReferenceImages: [],
      imageCount,
      style,
      quality,
      model,
      systemContextOverride: systemContextEdited?.trim() || undefined,
    });
  }, [selectedComponent, prompt, imageCount, style, quality, model, onGenerate]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleGenerate();
      }
    },
    [handleGenerate]
  );

  if (!selectedComponent) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
        <div className="text-center">
          <Sparkles size={32} className="mx-auto mb-2 opacity-40" />
          <p>Select a component to start</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto p-4 gap-4">
      <div className="flex items-center gap-2 text-sm font-medium text-zinc-200">
        <Pin size={14} className="text-blue-400" />
        <span>Selected: {selectedComponent.name}</span>
      </div>

      {/* System context (auto-generated, editable) — shows exact API text */}
      {systemContextText && (
        <div className="space-y-1">
          <label className="text-[12px] text-zinc-500 font-medium uppercase tracking-wider">
            System context (auto-generated, editable)
          </label>
          <textarea
            ref={systemTextareaRef}
            value={systemContextValue}
            onChange={(e) => setSystemContextEdited(e.target.value)}
            className="w-full bg-zinc-800/30 border border-zinc-700/30 rounded-lg p-2.5 text-xs text-zinc-400 leading-relaxed whitespace-pre-wrap resize-none overflow-hidden focus:outline-none focus:text-zinc-200 focus:border-zinc-600/50"
          />
        </div>
      )}

      <div className="space-y-1.5 flex-1">
        <label className="text-xs text-zinc-400 font-medium">Your prompt</label>
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={handlePromptChange}
          onKeyDown={handleKeyDown}
          placeholder={`Describe the ${selectedComponent.name} asset you want to generate...`}
          className="w-full flex-1 min-h-[100px] bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-sm text-zinc-200 placeholder:text-zinc-600 resize-y focus:outline-none focus:border-blue-500/50"
        />
        <div className="text-[10px] text-zinc-600">Enter to generate, Shift+Enter for new line</div>
      </div>

      <div className="space-y-1.5">
        <label className="text-xs text-zinc-400 font-medium">Number of images</label>
        <div className="flex gap-2">
          {IMAGE_COUNT_OPTIONS.map((count) => (
            <button
              key={count}
              onClick={() => setImageCount(count)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors
                ${imageCount === count
                  ? 'bg-blue-600 text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-zinc-700'
                }`}
            >
              {count}
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={handleGenerate}
        disabled={!prompt.trim()}
        className="w-full py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
      >
        <Sparkles size={14} />
        Generate ({imageCount} image{imageCount > 1 ? 's' : ''})
      </button>
    </div>
  );
};
